import React, { useCallback, useEffect, useState } from "react";
import { BarChart3, RefreshCw, TrendingUp, Wallet, HandCoins, Car, UtensilsCrossed, Package, Users, FileDown, Receipt } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { PageHeader, LoadingBlock, StatCard } from "../../components/app/ui";
import { adminApi, payoutApi, errorMessage, inr2 } from "../../lib/api";

const money = (n) => inr2(Number(n || 0));

function Section({ title, icon: Icon, children, testid }) {
  return (
    <section className="mt-8" data-testid={testid}>
      <h2 className="mb-3 flex items-center gap-2 text-lg font-bold text-ink"><Icon className="h-5 w-5 text-brand" /> {title}</h2>
      {children}
    </section>
  );
}

function Table({ head, rows }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-surface-300">
      <table className="w-full text-sm">
        <thead className="bg-surface-100 text-left text-xs uppercase tracking-wider text-ink-muted">
          <tr>{head.map((h, i) => <th key={i} className={`px-4 py-3 ${i === 0 ? "" : "text-right"}`}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-surface-200" data-testid={r.testid}>
              {r.cells.map((c, j) => <td key={j} className={`px-4 py-3 ${j === 0 ? "font-medium text-ink" : "text-right text-ink-soft"} ${r.strong && j === r.cells.length - 1 ? "font-bold text-ink" : ""}`}>{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function FinancialReport() {
  const [data, setData] = useState(null);
  const [gst, setGst] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const monthRange = (offset = 0) => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    const end = new Date(now.getFullYear(), now.getMonth() + offset + 1, 0); // last day of month
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return { from: fmt(start), to: fmt(end) };
  };
  const setMonth = (offset) => { const r = monthRange(offset); setFrom(r.from); setTo(r.to); };

  const load = useCallback(() => {
    setData(null);
    const params = {};
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    adminApi.financialReport(params).then(setData).catch((e) => { toast.error(errorMessage(e)); setData(false); });
    payoutApi.gstRemittance(params).then(setGst).catch(() => setGst(false));
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const c = data?.completed;
  const p = data?.profit;
  const pay = data?.payables;
  const cod = data?.cod;

  return (
    <>
      <PageHeader
        title="Financial report"
        description={data?.period ? `Period: ${data.period.from} → ${data.period.to}${data.period.label ? ` · ${data.period.label}` : ""}` : "Platform revenue, commission, payables and cash reconciliation."}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => setMonth(0)} data-testid="fin-this-month" className="rounded-full border border-brand px-3 py-1.5 text-xs font-bold text-brand hover:bg-brand-50">This month</button>
            <button onClick={() => setMonth(-1)} data-testid="fin-last-month" className="rounded-full border border-brand px-3 py-1.5 text-xs font-bold text-brand hover:bg-brand-50">Last month</button>
            <label className="flex items-center gap-1.5 text-xs text-ink-soft">From
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="fin-date-from" className="rounded-lg border border-surface-300 bg-white px-2 py-1.5 text-sm text-ink focus:border-brand focus:outline-none" />
            </label>
            <label className="flex items-center gap-1.5 text-xs text-ink-soft">To
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="fin-date-to" className="rounded-lg border border-surface-300 bg-white px-2 py-1.5 text-sm text-ink focus:border-brand focus:outline-none" />
            </label>
            {(from || to) && <button onClick={() => { setFrom(""); setTo(""); }} data-testid="fin-clear" className="text-xs font-semibold text-brand hover:underline">This week</button>}
            <Button variant="outline" onClick={load} className="rounded-full" data-testid="fin-refresh"><RefreshCw className="mr-2 h-4 w-4" /> Refresh</Button>
          </div>
        }
      />

      {data === null ? (
        <LoadingBlock rows={6} />
      ) : data === false ? (
        <p className="text-ink-muted">Could not load the report.</p>
      ) : (
        <div data-testid="financial-report">
          {/* Headline cards */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label="Completed orders" value={c.total_count} icon={BarChart3} tone="brand" />
            <div data-testid="fin-profit-total"><StatCard label="Commission / profit" value={money(p.total_commission_profit)} icon={TrendingUp} tone="graphite" /></div>
            <div data-testid="fin-payable-total"><StatCard label="Owed to partners" value={money(pay.total_payable)} icon={Wallet} tone="graphite" /></div>
            <div data-testid="fin-cash-held"><StatCard label="Cash with partners" value={money(cod.total_cash_with_partners_now)} icon={HandCoins} tone="graphite" /></div>
          </div>

          {/* Completed orders breakdown */}
          <Section title="Completed orders this period" icon={BarChart3} testid="fin-completed-section">
            <Table
              head={["Service", "Count", "Gross", "Platform earns"]}
              rows={[
                { testid: "fin-row-rides", cells: ["Taxi rides", c.rides.count, money(c.rides.gross), money(c.rides.platform_fee)] },
                { testid: "fin-row-goods", cells: ["Goods delivery", c.goods.count, money(c.goods.gross), money(c.goods.platform_fee)] },
                { testid: "fin-row-food", cells: ["Food orders", c.food.count, money(c.food.gross_total), money(c.food.net_commission ?? c.food.restaurant_commission)] },
                { testid: "fin-row-pc", cells: ["Private driver", c.private_car.count, "—", money(c.private_car.platform_fee)] },
                { testid: "fin-row-total", strong: true, cells: ["Total", c.total_count, "", money(p.total_commission_profit)] },
              ]}
            />
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              <div className="kt-card p-4"><p className="text-xs uppercase tracking-wider text-ink-muted">Food subtotal</p><p className="mt-1 text-xl font-bold text-ink">{money(c.food.food_subtotal)}</p></div>
              <div className="kt-card p-4"><p className="text-xs uppercase tracking-wider text-ink-muted">Packaging + GST collected</p><p className="mt-1 text-xl font-bold text-ink">{money(c.food.packaging + c.food.gst)}</p></div>
              <div className="kt-card p-4"><p className="text-xs uppercase tracking-wider text-ink-muted">Delivery fees</p><p className="mt-1 text-xl font-bold text-ink">{money(c.food.delivery_fees)}</p></div>
            </div>
          </Section>

          {/* Profit breakdown */}
          <Section title="Commission & profit" icon={TrendingUp} testid="fin-profit-section">
            <Table
              head={["Source", "Amount"]}
              rows={[
                { cells: ["Ride platform fees", money(p.ride_platform_fees)] },
                { testid: "fin-goods-commission", cells: ["Goods commission collected", money(p.goods_platform_fees)] },
                { cells: ["Restaurant commission (gross 13%)", money(p.restaurant_commission)] },
                { testid: "fin-food-discounts", cells: ["− Customer food discounts funded", `−${money(p.food_customer_discounts || 0)}`] },
                { testid: "fin-food-net", cells: ["Food commission kept (net)", money(p.food_net_commission ?? p.restaurant_commission)] },
                { cells: ["Private-driver platform fees", money(p.private_car_platform_fees)] },
                { strong: true, cells: ["Total commission / profit", money(p.total_commission_profit)] },
              ]}
            />
          </Section>

          {/* GST remittance */}
          <Section title="GST collected (for remittance)" icon={Receipt} testid="fin-gst-section">
            {gst && gst.customer_gst ? (
              <>
                <Table
                  head={["GST bucket", "Base", "GST"]}
                  rows={[
                    { testid: "gst-cust-taxi", cells: ["Customer 5% — Taxi", "—", money(gst.customer_gst.taxi)] },
                    { testid: "gst-cust-goods", cells: ["Customer 5% — Goods", "—", money(gst.customer_gst.goods)] },
                    { testid: "gst-cust-food", cells: ["Customer 5% — Food", "—", money(gst.customer_gst.food)] },
                    { testid: "gst-cust-pc", cells: ["Customer 5% — Private-car platform fee", "—", money(gst.customer_gst.private_car_platform_fee)] },
                    { testid: "gst-commission", cells: ["Commission GST (18% of commission)", money(gst.commission_gst.commission_base), money(gst.commission_gst.amount)] },
                    { testid: "gst-total", strong: true, cells: ["Total GST collected", "", money(gst.total_gst_collected)] },
                  ]}
                />
                <div className="mt-3 flex flex-wrap gap-2">
                  <a href={payoutApi.gstRemittanceCsvUrl({ ...(from ? { from } : {}), ...(to ? { to } : {}) })} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-brand px-3 py-1.5 text-xs font-bold text-brand hover:bg-brand-50" data-testid="gst-remittance-csv"><FileDown className="h-3.5 w-3.5" /> Remittance CSV</a>
                  <a href={payoutApi.gstRemittancePdfUrl({ ...(from ? { from } : {}), ...(to ? { to } : {}) })} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-brand px-3 py-1.5 text-xs font-bold text-brand hover:bg-brand-50" data-testid="gst-remittance-pdf"><FileDown className="h-3.5 w-3.5" /> Remittance PDF</a>
                  <a href={payoutApi.commissionGstCsvUrl()} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-brand px-3 py-1.5 text-xs font-bold text-brand hover:bg-brand-50" data-testid="gst-commission-csv"><FileDown className="h-3.5 w-3.5" /> Commission GST export (this month)</a>
                </div>
              </>
            ) : <p className="text-sm text-ink-muted">GST totals unavailable for this period.</p>}
          </Section>

          {/* Payables */}
          <Section title="Partner balances (as of now)" icon={Users} testid="fin-payables-section">
            <Table
              head={["Balance", "Amount"]}
              rows={[
                { cells: ["Owed to drivers", money(pay.drivers_payable)] },
                { cells: ["Owed to restaurants", money(pay.restaurants_payable)] },
                { strong: true, cells: ["Total payable by platform", money(pay.total_payable)] },
                { cells: ["Partners owe the platform", money(pay.partners_owe_platform)] },
              ]}
            />
          </Section>

          {/* COD */}
          <Section title="COD & cash reconciliation" icon={HandCoins} testid="fin-cod-section">
            <Table
              head={["Item", "Amount"]}
              rows={[
                { cells: ["Food cash held by drivers now", money(cod.food_cash_outstanding_now)] },
                { cells: ["Ride cash held by drivers now", money(cod.ride_cash_held_now)] },
                { strong: true, cells: ["Total cash with delivery partners", money(cod.total_cash_with_partners_now)] },
                { cells: ["COD deposits awaiting verification (period)", money(cod.deposits_submitted_period)] },
                { cells: ["COD deposits settled (period)", money(cod.deposits_settled_period)] },
              ]}
            />
          </Section>
        </div>
      )}
    </>
  );
}
